import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, Maximize2, Copy, Check, X, ChevronDown,
  Square, RotateCw, Layers, ArrowLeft, Save, Monitor,
} from "lucide-react";
import type { CanvasGroup, AnimationInstance } from "@/lib/animation/types";
import { computeCanvasGroupGeometry } from "@/lib/animation/canvasGroupGeometry";
import { CanvasGroupPanel } from "@/components/wallplanner/CanvasGroupPanel";
import { AnimationTimeline } from "@/components/wallplanner/AnimationTimeline";

// ── Presets ──────────────────────────────────────────────────────────────
const PRESETS: Record<string, { w: number; h: number; bt: number; br: number; bb: number; bl: number; nw: number; nh: number }> = {
  // 24"
  '24" 1080p':  { w: 531, h: 299, bt: 10, br: 10, bb: 22, bl: 10, nw: 1920, nh: 1080 },
  '24" 1440p':  { w: 531, h: 299, bt: 10, br: 10, bb: 22, bl: 10, nw: 2560, nh: 1440 },
  '24" 4K':     { w: 531, h: 299, bt: 10, br: 10, bb: 22, bl: 10, nw: 3840, nh: 2160 },
  // 27"
  '27" 1080p':  { w: 597, h: 336, bt: 12, br: 12, bb: 25, bl: 12, nw: 1920, nh: 1080 },
  '27" 1440p':  { w: 597, h: 336, bt: 12, br: 12, bb: 25, bl: 12, nw: 2560, nh: 1440 },
  '27" 4K':     { w: 597, h: 336, bt: 12, br: 12, bb: 25, bl: 12, nw: 3840, nh: 2160 },
  // 32"
  '32" 1080p':  { w: 708, h: 398, bt: 12, br: 12, bb: 28, bl: 12, nw: 1920, nh: 1080 },
  '32" 1440p':  { w: 708, h: 398, bt: 12, br: 12, bb: 28, bl: 12, nw: 2560, nh: 1440 },
  '32" 4K':     { w: 708, h: 398, bt: 12, br: 12, bb: 28, bl: 12, nw: 3840, nh: 2160 },
  // 34" ultrawide (21:9)
  '34" 1080p UW':  { w: 800, h: 343, bt: 12, br: 12, bb: 28, bl: 12, nw: 2560, nh: 1080 },
  '34" 1440p UW':  { w: 800, h: 343, bt: 12, br: 12, bb: 28, bl: 12, nw: 3440, nh: 1440 },
  // 49" super-ultrawide (32:9 — dual 4K side by side)
  '49" 1080p DUW': { w: 1196, h: 336, bt: 14, br: 14, bb: 32, bl: 14, nw: 3840, nh: 1080 },
  '49" 1440p DUW': { w: 1196, h: 336, bt: 14, br: 14, bb: 32, bl: 14, nw: 5120, nh: 1440 },
  '49" 2x4K DUW':  { w: 1196, h: 336, bt: 14, br: 14, bb: 32, bl: 14, nw: 7680, nh: 2160 },
  // 55" TV/display (16:9)
  '55" 1080p':  { w: 1218, h: 685, bt: 16, br: 16, bb: 35, bl: 16, nw: 1920, nh: 1080 },
  '55" 4K':     { w: 1218, h: 685, bt: 16, br: 16, bb: 35, bl: 16, nw: 3840, nh: 2160 },
  // 65" TV/display (16:9)
  '65" 1080p':  { w: 1439, h: 809, bt: 18, br: 18, bb: 40, bl: 18, nw: 1920, nh: 1080 },
  '65" 4K':     { w: 1439, h: 809, bt: 18, br: 18, bb: 40, bl: 18, nw: 3840, nh: 2160 },
};

interface MonitorDef {
  id: string; name: string; x: number; y: number; preset: string;
  w: number; h: number; bt: number; br: number; bb: number; bl: number; nw: number; nh: number;
}
interface WallDef {
  id: string; name: string; origin: { x: number; y: number }; rotation: number; monitors: MonitorDef[];
}
import {
  visibleBounds,
  totalBounds,
  wallToWorld,
  worldToWall,
  normRot,
  type Bounds,
} from '@/lib/wallGeometry';

const makeMonitor = (id: string, name: string, x: number, y: number, preset = '27" 1440p'): MonitorDef => ({
  id, name, x, y, preset, ...PRESETS[preset],
});

const makeWall = (id: string, name: string, ox: number, oy: number, rotation: number, monitors: MonitorDef[]): WallDef => ({
  id, name, origin: { x: ox, y: oy }, rotation, monitors,
});


// ── Geometry ─────────────────────────────────────────────────────────────
// Pure coordinate maths, extracted to @/lib/wallGeometry so it can be tested.
// MonitorDef and WallDef satisfy the structural types it declares.

const allWorldBounds = (walls: WallDef[]): Bounds => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const wall of walls) {
    if (!wall.monitors.length) continue;
    const tb = totalBounds(wall.monitors);
    const corners: [number, number][] = [[tb.x, tb.y], [tb.x + tb.w, tb.y],
                     [tb.x, tb.y + tb.h], [tb.x + tb.w, tb.y + tb.h]];
    for (const [lx, ly] of corners) {
      const wp = wallToWorld(wall, lx, ly);
      minX = Math.min(minX, wp.x); minY = Math.min(minY, wp.y);
      maxX = Math.max(maxX, wp.x); maxY = Math.max(maxY, wp.y);
    }
  }
  if (minX === Infinity) return { x: 0, y: 0, w: 4000, h: 2000 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

const computeSnap = (drag: MonitorDef, others: MonitorDef[], threshold = 12) => {
  const dEdgesX = [drag.x, drag.x + drag.w / 2, drag.x + drag.w];
  const dEdgesY = [drag.y, drag.y + drag.h / 2, drag.y + drag.h];
  let bestDx = 0, bestDy = 0, foundX = false, foundY = false;
  for (const m of others) {
    const tEdgesX = [m.x, m.x + m.w / 2, m.x + m.w];
    const tEdgesY = [m.y, m.y + m.h / 2, m.y + m.h];
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
      const dx = tEdgesX[j] - dEdgesX[i];
      if (Math.abs(dx) < threshold && (!foundX || Math.abs(dx) < Math.abs(bestDx))) {
        bestDx = dx; foundX = true;
      }
      const dy = tEdgesY[j] - dEdgesY[i];
      if (Math.abs(dy) < threshold && (!foundY || Math.abs(dy) < Math.abs(bestDy))) {
        bestDy = dy; foundY = true;
      }
    }
  }
  return { dx: foundX ? bestDx : 0, dy: foundY ? bestDy : 0 };
};

interface Guide { type: 'v' | 'h'; x?: number; y?: number; x1?: number; y1?: number; x2?: number; y2?: number; }
interface DistLabel { type: 'h' | 'v'; dist: number; x1: number; y1: number; x2: number; y2: number; }

const computeDistances = (drag: MonitorDef, others: MonitorDef[]): DistLabel[] => {
  const labels: DistLabel[] = [];
  const dL = drag.x - drag.bl, dR = drag.x + drag.w + drag.br;
  const dT = drag.y - drag.bt, dB = drag.y + drag.h + drag.bb;
  for (const m of others) {
    const mL = m.x - m.bl, mR = m.x + m.w + m.br;
    const mT = m.y - m.bt, mB = m.y + m.h + m.bb;
    // Horizontal gap: dragged right edge to other left edge
    if (dR < mL) {
      const midY = (Math.max(dT, mT) + Math.min(dB, mB)) / 2;
      if (Math.max(dT, mT) < Math.min(dB, mB)) // vertically overlapping
        labels.push({ type: 'h', dist: Math.round(mL - dR), x1: dR, y1: midY, x2: mL, y2: midY });
    }
    // Horizontal gap: other right edge to dragged left edge
    if (mR < dL) {
      const midY = (Math.max(dT, mT) + Math.min(dB, mB)) / 2;
      if (Math.max(dT, mT) < Math.min(dB, mB))
        labels.push({ type: 'h', dist: Math.round(dL - mR), x1: mR, y1: midY, x2: dL, y2: midY });
    }
    // Vertical gap: dragged bottom to other top
    if (dB < mT) {
      const midX = (Math.max(dL, mL) + Math.min(dR, mR)) / 2;
      if (Math.max(dL, mL) < Math.min(dR, mR))
        labels.push({ type: 'v', dist: Math.round(mT - dB), x1: midX, y1: dB, x2: midX, y2: mT });
    }
    // Vertical gap: other bottom to dragged top
    if (mB < dT) {
      const midX = (Math.max(dL, mL) + Math.min(dR, mR)) / 2;
      if (Math.max(dL, mL) < Math.min(dR, mR))
        labels.push({ type: 'v', dist: Math.round(dT - mB), x1: midX, y1: mB, x2: midX, y2: dT });
    }
  }
  return labels;
};

const activeGuides = (drag: MonitorDef, others: MonitorDef[], eps = 0.5): Guide[] => {
  const guides: Guide[] = [];
  const dx = [drag.x, drag.x + drag.w / 2, drag.x + drag.w];
  const dy = [drag.y, drag.y + drag.h / 2, drag.y + drag.h];
  for (const m of others) {
    const tx = [m.x, m.x + m.w / 2, m.x + m.w];
    const ty = [m.y, m.y + m.h / 2, m.y + m.h];
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
      if (Math.abs(dx[i] - tx[j]) < eps) {
        guides.push({
          type: 'v', x: tx[j],
          y1: Math.min(drag.y - drag.bt, m.y - m.bt) - 50,
          y2: Math.max(drag.y + drag.h + drag.bb, m.y + m.h + m.bb) + 50,
        });
      }
      if (Math.abs(dy[i] - ty[j]) < eps) {
        guides.push({
          type: 'h', y: ty[j],
          x1: Math.min(drag.x - drag.bl, m.x - m.bl) - 50,
          x2: Math.max(drag.x + drag.w + drag.br, m.x + m.w + m.br) + 50,
        });
      }
    }
  }
  return guides;
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// ── Test pattern ─────────────────────────────────────────────────────────
const TestPattern = ({ bounds, hueBase = 180 }: { bounds: Bounds; hueBase?: number }) => {
  const { x, y, w, h } = bounds;
  const cols = 12;
  const segW = w / cols;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#0a1218" />
      {Array.from({ length: cols }).map((_, i) => {
        const t = i / cols;
        const hue = hueBase + t * 60;
        return (
          <rect key={i} x={x + i * segW} y={y} width={segW} height={h}
            fill={`hsl(${hue}, 60%, ${30 + (i % 2) * 8}%)`} opacity="0.55" />
        );
      })}
      <line x1={x} y1={y} x2={x + w} y2={y + h} stroke="#fbbf24"
        strokeWidth={Math.max(8, w * 0.003)} opacity="0.85" />
      <line x1={x} y1={y + h} x2={x + w} y2={y} stroke="#22d3ee"
        strokeWidth={Math.max(8, w * 0.003)} opacity="0.85" />
      {[0.25, 0.5, 0.75].map((t) => (
        <line key={t} x1={x} y1={y + h * t} x2={x + w} y2={y + h * t}
          stroke="rgba(255,255,255,0.18)" strokeWidth={2} />
      ))}
      <circle cx={x + w / 2} cy={y + h / 2} r={Math.min(w, h) * 0.04}
        fill="none" stroke="#f1f5f9" strokeWidth={3} opacity="0.7" />
    </g>
  );
};

// ── Inputs / sections ────────────────────────────────────────────────────
const NumInput = ({ label, value, onChange, suffix = "mm", step = 1 }: { label: string; value: number; onChange: (v: number) => void; suffix?: string; step?: number }) => (
  <label className="flex items-center justify-between gap-2 text-xs">
    <span className="text-slate-400 font-mono uppercase tracking-wider text-[10px]">{label}</span>
    <div className="flex items-center gap-1">
      <input type="number" value={value} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500" />
      <span className="text-slate-500 font-mono text-[10px] w-6">{suffix}</span>
    </div>
  </label>
);

const TextInput = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <label className="flex items-center justify-between gap-2 text-xs">
    <span className="text-slate-400 font-mono uppercase tracking-wider text-[10px]">{label}</span>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-32 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500" />
  </label>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value, accent }: { label: string; value: string | number; accent?: string | null }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-[11px] text-slate-400 font-mono">{label}</span>
    <span className={`text-xs font-mono ${accent === 'warn' ? 'text-amber-400' : 'text-slate-100'}`}>{value}</span>
  </div>
);

// ── Main ────────────────────────────────────────────────────────────────
export function WallPlannerPage() {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();

  // Fetch location details + saved wall config
  // gcTime: 0 ensures cache is cleared on unmount so navigating back always fetches fresh data
  const { data: location, isLoading: locLoading } = useQuery({
    queryKey: ['wall-planner-location', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('demo_locations')
        .select('id, name, shop_code, wall_config')
        .eq('id', locationId!)
        .single();
      if (error) throw error;
      return data as { id: string; name: string; shop_code: string; wall_config: Record<string, unknown> | null };
    },
    enabled: !!locationId,
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch stations assigned to this location
  const { data: stations = [], isLoading: stationsLoading } = useQuery({
    queryKey: ['wall-planner-stations', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('demo_stations')
        .select('id, name, machine_id')
        .eq('location_id', locationId!)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string; machine_id: string }[];
    },
    enabled: !!locationId,
  });

  // Save wall config to DB
  const saveMutation = useMutation({
    mutationFn: async (config: Record<string, unknown>) => {
      const { error } = await supabase
        .from('demo_locations')
        .update({ wall_config: config })
        .eq('id', locationId!);
      if (error) throw error;
    },
    onSuccess: () => toast.success('Wall layout saved'),
    onError: (err: Error) => toast.error('Save failed: ' + err.message),
  });

  const [walls, setWalls] = useState<WallDef[]>([]);
  const [initialised, setInitialised] = useState(false);
  const [selected, setSelected] = useState<{ wallId: string | null; monitorId: string | null }>({ wallId: null, monitorId: null });
  const [view, setView] = useState({ tx: 80, ty: 200, zoom: 0.13 });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [drag, setDrag] = useState<any>(null);
  const [guides, setGuides] = useState<{ wallId: string | null; list: Guide[] }>({ wallId: null, list: [] });
  const [distances, setDistances] = useState<{ wallId: string | null; list: DistLabel[] }>({ wallId: null, list: [] });
  const [showAddStation, setShowAddStation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'properties' | 'stations' | 'groups'>('properties');
  const [canvasGroups, setCanvasGroups] = useState<CanvasGroup[]>([]);
  const [animations, setAnimations] = useState<AnimationInstance[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [bottomPanel, setBottomPanel] = useState<'export' | 'animation'>('export');
  const [animPreviewFrames, setAnimPreviewFrames] = useState<Map<string, string>>(new Map());
  const svgRef = useRef<SVGSVGElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Initialise walls from saved config or generate from stations
  useEffect(() => {
    if (initialised || locLoading || stationsLoading || !location) return;
    const saved = location.wall_config as any;
    if (saved?.walls?.length) {
      // Restore from saved config
      const restored: WallDef[] = saved.walls.map((w: any) => ({
        id: w.id,
        name: w.name,
        origin: w.origin ?? { x: 0, y: 0 },
        rotation: w.rotation ?? 0,
        monitors: (w.monitors ?? []).map((m: any) => ({
          id: m.id, name: m.name, x: m.x ?? 0, y: m.y ?? 0,
          preset: m.preset ?? '27" 1440p',
          w: m.w, h: m.h, bt: m.bt, br: m.br, bb: m.bb, bl: m.bl, nw: m.nw, nh: m.nh,
          stationId: m.stationId ?? null,
        })),
      }));
      setWalls(restored);
      setSelected({ wallId: restored[0]?.id ?? null, monitorId: null });
      // Restore canvas groups and animations
      if (saved.canvas_groups) setCanvasGroups(saved.canvas_groups);
      if (saved.animations) setAnimations(saved.animations);
    } else if (stations.length) {
      // Auto-generate one wall with all stations
      const mons = stations.map((s, i) => ({
        ...makeMonitor(`m-${s.id}`, s.name, i * 1100, 0),
        stationId: s.id,
      }));
      const w = makeWall('w1', 'Main Wall', 0, 0, 0, mons);
      setWalls([w]);
      setSelected({ wallId: 'w1', monitorId: null });
    } else {
      setWalls([makeWall('w1', 'Main Wall', 0, 0, 0, [])]);
      setSelected({ wallId: 'w1', monitorId: null });
    }
    setInitialised(true);
  }, [location, stations, locLoading, stationsLoading, initialised]);

  // Track dirty state when walls/groups/animations change after initialisation
  const wallsRef = useRef(walls);
  const groupsRef = useRef(canvasGroups);
  const animsRef = useRef(animations);
  useEffect(() => {
    if (!initialised) return;
    if (wallsRef.current !== walls || groupsRef.current !== canvasGroups || animsRef.current !== animations) {
      setDirty(true);
      wallsRef.current = walls;
      groupsRef.current = canvasGroups;
      animsRef.current = animations;
    }
  }, [walls, canvasGroups, animations, initialised]);

  const saveConfig = useCallback(() => {
    const cfg = {
      version: 3,
      walls: walls.map((w) => ({
        id: w.id, name: w.name, origin: w.origin, rotation: w.rotation,
        monitors: w.monitors.map((m) => ({
          id: m.id, stationId: (m as any).stationId ?? null,
          name: m.name, x: m.x, y: m.y, preset: m.preset,
          w: m.w, h: m.h, bt: m.bt, br: m.br, bb: m.bb, bl: m.bl, nw: m.nw, nh: m.nh,
        })),
      })),
      canvas_groups: canvasGroups,
      animations,
    };
    saveMutation.mutate(cfg as any);
    setDirty(false);
  }, [walls, canvasGroups, animations, saveMutation]);

  const selectedWall = walls.find((w) => w.id === selected.wallId) ?? null;
  const selectedMonitor = selectedWall?.monitors.find((m) => m.id === selected.monitorId) ?? null;
  const isMonSel = !!selectedMonitor;
  const isWallSel = !!selectedWall && !selectedMonitor;

  const worldBB = useMemo(() => allWorldBounds(walls), [walls]);

  const wallStats = useCallback((wall: WallDef) => {
    if (!wall.monitors.length) return null;
    const vb = visibleBounds(wall.monitors);
    const density = Math.max(...wall.monitors.map((m) => Math.max(m.nw / m.w, m.nh / m.h)));
    const visibleArea = wall.monitors.reduce((s, m) => s + m.w * m.h, 0);
    const totalArea = vb.w * vb.h;
    const deadPct = totalArea > 0 ? (1 - visibleArea / totalArea) * 100 : 0;
    return { vb, density, masterW: Math.round(vb.w * density), masterH: Math.round(vb.h * density), deadPct };
  }, []);

  const focusedStats = useMemo(() => selectedWall ? wallStats(selectedWall) : null,
    [selectedWall, wallStats]);

  const screenToWorld = useCallback((sx: number, sy: number) => ({
    x: (sx - view.tx) / view.zoom,
    y: (sy - view.ty) / view.zoom,
  }), [view]);

  const fitView = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const { width, height } = svg.getBoundingClientRect();
    const pad = 80;
    const zoom = Math.min((width - pad * 2) / worldBB.w, (height - pad * 2) / worldBB.h);
    setView({
      tx: width / 2 - (worldBB.x + worldBB.w / 2) * zoom,
      ty: height / 2 - (worldBB.y + worldBB.h / 2) * zoom,
      zoom: clamp(zoom, 0.02, 4),
    });
  }, [worldBB]);

  useEffect(() => {
    if (!initialised) return;
    const t = setTimeout(fitView, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialised]);

  const updateMonitor = (wallId: string, monId: string, patch: Partial<MonitorDef>) => {
    setWalls((ws) => ws.map((w) => w.id !== wallId ? w : {
      ...w, monitors: w.monitors.map((m) => m.id === monId ? { ...m, ...patch } : m),
    }));
  };
  const updateWall = (wallId: string, patch: Partial<WallDef>) => {
    setWalls((ws) => ws.map((w) => w.id === wallId ? { ...w, ...patch } : w));
  };

  // ── Pointer handlers ────
  const onPointerDownCanvas = (e: React.PointerEvent) => {
    if (e.target === svgRef.current || (e.target as Element).classList?.contains('canvas-bg')) {
      setSelected({ wallId: null, monitorId: null });
      setDrag({ type: 'pan', startX: e.clientX, startY: e.clientY, startView: { ...view } });
    }
  };

  const onPointerDownMonitor = (e: React.PointerEvent, wallId: string, monId: string) => {
    e.stopPropagation();
    setSelected({ wallId, monitorId: monId });
    const wall = walls.find((w) => w.id === wallId)!;
    const mon = wall.monitors.find((m) => m.id === monId)!;
    const rect = svgRef.current!.getBoundingClientRect();
    const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const local = worldToWall(wall, world.x, world.y);
    setDrag({
      type: 'monitor', wallId, monitorId: monId,
      offsetX: local.x - mon.x, offsetY: local.y - mon.y,
    });
  };

  const onPointerDownWall = (e: React.PointerEvent, wallId: string) => {
    e.stopPropagation();
    setSelected({ wallId, monitorId: null });
    const wall = walls.find((w) => w.id === wallId)!;
    const rect = svgRef.current!.getBoundingClientRect();
    const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    setDrag({
      type: 'wall', wallId,
      offsetX: world.x - wall.origin.x, offsetY: world.y - wall.origin.y,
    });
  };

  const onPointerDownRotate = (e: React.PointerEvent, wallId: string) => {
    e.stopPropagation();
    setSelected({ wallId, monitorId: null });
    const wall = walls.find((w) => w.id === wallId)!;
    const rect = svgRef.current!.getBoundingClientRect();
    const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const angle0 = Math.atan2(world.y - wall.origin.y, world.x - wall.origin.x);
    setDrag({ type: 'rotate', wallId, angle0, rotation0: wall.rotation });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const rect = svgRef.current!.getBoundingClientRect();
    if (drag.type === 'pan') {
      setView({
        ...drag.startView,
        tx: drag.startView.tx + (e.clientX - drag.startX),
        ty: drag.startView.ty + (e.clientY - drag.startY),
      });
    } else if (drag.type === 'monitor') {
      const wall = walls.find((w) => w.id === drag.wallId)!;
      const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      const local = worldToWall(wall, world.x, world.y);
      let target = { x: local.x - drag.offsetX, y: local.y - drag.offsetY };
      const dragMon = wall.monitors.find((m) => m.id === drag.monitorId)!;
      const others = wall.monitors.filter((m) => m.id !== drag.monitorId);
      const snap = computeSnap({ ...dragMon, ...target }, others, 12 / view.zoom);
      target = { x: Math.round(target.x + snap.dx), y: Math.round(target.y + snap.dy) };
      updateMonitor(drag.wallId, drag.monitorId, target);
      const snapped = { ...dragMon, ...target };
      setGuides({ wallId: drag.wallId, list: activeGuides(snapped, others) });
      setDistances({ wallId: drag.wallId, list: computeDistances(snapped, others) });
    } else if (drag.type === 'wall') {
      const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      updateWall(drag.wallId, {
        origin: { x: Math.round(world.x - drag.offsetX), y: Math.round(world.y - drag.offsetY) },
      });
    } else if (drag.type === 'rotate') {
      const wall = walls.find((w) => w.id === drag.wallId)!;
      const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      const angle = Math.atan2(world.y - wall.origin.y, world.x - wall.origin.x);
      let newRot = drag.rotation0 + ((angle - drag.angle0) * 180) / Math.PI;
      if (!e.shiftKey) {
        const snapped = Math.round(newRot / 15) * 15;
        if (Math.abs(snapped - newRot) < 3) newRot = snapped;
      }
      updateWall(drag.wallId, { rotation: normRot(newRot) });
    }
  };

  const onPointerUp = () => { setDrag(null); setGuides({ wallId: null, list: [] }); setDistances({ wallId: null, list: [] }); };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = svgRef.current!.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = clamp(view.zoom * factor, 0.02, 4);
    const wx = (sx - view.tx) / view.zoom, wy = (sy - view.ty) / view.zoom;
    setView({ tx: sx - wx * newZoom, ty: sy - wy * newZoom, zoom: newZoom });
  };

  const addStation = (preset: string) => {
    let targetId = selected.wallId ?? walls[0]?.id;
    if (!targetId) {
      const id = `w${Date.now()}`;
      setWalls([makeWall(id, 'Wall 1', 0, 0, 0, [])]);
      targetId = id;
    }
    const wall = walls.find((w) => w.id === targetId) ?? { monitors: [] as MonitorDef[] };
    const last = wall.monitors[wall.monitors.length - 1];
    const x = last ? last.x + last.w + 500 : 0;
    const y = last ? last.y : 0;
    const id = `s${Date.now()}`;
    const idx = walls.flatMap((w) => w.monitors).length + 1;
    const newMon = makeMonitor(id, `Station ${String(idx).padStart(2, '0')}`, x, y, preset);
    setWalls((ws) => ws.map((w) => w.id === targetId ? { ...w, monitors: [...w.monitors, newMon] } : w));
    setSelected({ wallId: targetId, monitorId: id });
    setShowAddStation(false);
  };

  const addWall = () => {
    const id = `w${Date.now()}`;
    const ox = walls.length ? worldBB.x + worldBB.w + 1500 : 0;
    const stationId = `s${Date.now()}`;
    const idx = walls.flatMap((w) => w.monitors).length + 1;
    const newWall = makeWall(id, `Wall ${walls.length + 1}`, ox, 0, 0, [
      makeMonitor(stationId, `Station ${String(idx).padStart(2, '0')}`, 0, 0),
    ]);
    setWalls([...walls, newWall]);
    setSelected({ wallId: id, monitorId: null });
  };

  const deleteSelected = () => {
    if (selectedMonitor) {
      const w = selectedWall!;
      const remaining = w.monitors.filter((m) => m.id !== selectedMonitor.id);
      setWalls((ws) => ws.map((ww) => ww.id === w.id ? { ...ww, monitors: remaining } : ww));
      setSelected({ wallId: w.id, monitorId: null });
    } else if (selectedWall) {
      setWalls((ws) => ws.filter((ww) => ww.id !== selectedWall.id));
      setSelected({ wallId: null, monitorId: null });
    }
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setPreviewImage(r.result as string);
    r.readAsDataURL(file);
  };

  const exportConfig = () => {
    const cfg = {
      version: 2,
      walls: walls.map((wall) => {
        const stats = wallStats(wall);
        return {
          id: wall.id, name: wall.name,
          origin_mm: wall.origin,
          rotation_deg: wall.rotation,
          master_canvas: stats ? { mm: stats.vb, render_px: { w: stats.masterW, h: stats.masterH } } : null,
          stations: wall.monitors.map((m) => ({
            id: m.id, name: m.name,
            position_mm: { x: m.x, y: m.y },
            physical_mm: { w: m.w, h: m.h },
            bezel_mm: { top: m.bt, right: m.br, bottom: m.bb, left: m.bl },
            native_px: { w: m.nw, h: m.nh },
            slice_uv: stats && stats.vb.w > 0 && stats.vb.h > 0 ? {
              u: (m.x - stats.vb.x) / stats.vb.w,
              v: (m.y - stats.vb.y) / stats.vb.h,
              uw: m.w / stats.vb.w,
              vh: m.h / stats.vb.h,
            } : null,
          })),
        };
      }),
    };
    navigator.clipboard.writeText(JSON.stringify(cfg, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const totalStations = walls.reduce((s, w) => s + w.monitors.length, 0);

  // ── Canvas Group helpers ────
  const addCanvasGroup = () => {
    const id = `cg-${Date.now()}`;
    const newGroup: CanvasGroup = { id, name: `Group ${canvasGroups.length + 1}`, monitors: [] };
    setCanvasGroups([...canvasGroups, newGroup]);
    setSelectedGroupId(id);
    setSidebarTab('groups');
  };
  const deleteCanvasGroup = (id: string) => {
    setCanvasGroups((gs) => gs.filter((g) => g.id !== id));
    setAnimations((as) => as.filter((a) => a.canvasGroupId !== id));
    if (selectedGroupId === id) setSelectedGroupId(null);
  };
  const updateCanvasGroup = (id: string, patch: Partial<CanvasGroup>) => {
    setCanvasGroups((gs) => gs.map((g) => g.id === id ? { ...g, ...patch } : g));
  };
  const getGroupGeometry = useCallback((group: CanvasGroup) => {
    return computeCanvasGroupGeometry(group, walls);
  }, [walls]);

  // Animation preview callbacks
  const handlePreviewFrame = useCallback((frames: Map<string, string>) => {
    setAnimPreviewFrames(frames);
  }, []);
  const handlePreviewClear = useCallback(() => {
    setAnimPreviewFrames(new Map());
  }, []);

  // Track which DB stations are already placed on the canvas
  const placedStationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const w of walls) for (const m of w.monitors) {
      if ((m as any).stationId) ids.add((m as any).stationId);
    }
    return ids;
  }, [walls]);

  // Handle drop of a station from the sidebar list onto the SVG canvas
  const onDropStation = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const json = e.dataTransfer.getData('application/json');
    if (!json) return;
    const { stationId, stationName } = JSON.parse(json);
    if (placedStationIds.has(stationId)) return;

    // Find the target wall — prefer selected, else first
    let targetWallId = selected.wallId ?? walls[0]?.id;
    if (!targetWallId) {
      const wid = `w${Date.now()}`;
      setWalls([makeWall(wid, 'Main Wall', 0, 0, 0, [])]);
      targetWallId = wid;
    }
    const wall = walls.find((w) => w.id === targetWallId)!;

    // Convert drop position to wall-local coordinates
    const rect = svgRef.current!.getBoundingClientRect();
    const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const local = worldToWall(wall, world.x, world.y);

    const id = `m-${stationId}`;
    const preset = '27" 1440p';
    const p = PRESETS[preset];
    const newMon = { ...makeMonitor(id, stationName, Math.round(local.x - p.w / 2), Math.round(local.y - p.h / 2), preset), stationId };
    setWalls((ws) => ws.map((w) => w.id === targetWallId ? { ...w, monitors: [...w.monitors, newMon] } : w));
    setSelected({ wallId: targetWallId, monitorId: id });
    setSidebarTab('properties');
  }, [walls, selected.wallId, placedStationIds, screenToWorld]);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] w-full bg-slate-950 text-slate-100 overflow-hidden">
      <style>{`
        .canvas-bg { cursor: grab; }
        .canvas-bg:active { cursor: grabbing; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {locLoading && (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
        </div>
      )}
      {!locLoading && !location && (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <p className="text-slate-400">Location not found</p>
          <button onClick={() => navigate('/demo-stations')}
            className="text-cyan-400 hover:underline text-sm">Back to Demo Stations</button>
        </div>
      )}
      {!locLoading && location && (<>
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-900">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/demo-stations')}
            className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition" title="Back">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-cyan-400 rounded-full" />
            <span className="text-sm font-medium tracking-wide">Wall Planner</span>
          </div>
          <span className="text-slate-600">/</span>
          <span className="text-xs text-cyan-400 font-mono">{location.name}</span>
          <span className="text-slate-600">/</span>
          <span className="text-xs text-slate-400 font-mono">
            {walls.length} wall{walls.length !== 1 ? 's' : ''} &middot; {totalStations} station{totalStations !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={addWall}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded transition">
            <Layers size={14} /> Add Wall
          </button>
          <div className="relative">
            <button onClick={() => setShowAddStation(!showAddStation)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded font-medium transition">
              <Plus size={14} /> Add Station <ChevronDown size={12} />
            </button>
            {showAddStation && (
              <div className="absolute right-0 top-full mt-1 bg-slate-900 border border-slate-700 rounded shadow-xl z-50 min-w-48 max-h-80 overflow-y-auto">
                {Object.keys(PRESETS).map((p) => (
                  <button key={p} onClick={() => addStation(p)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-800 font-mono border-b border-slate-800 last:border-0">
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded transition">
            <Upload size={14} /> {previewImage ? "Replace" : "Image"}
          </button>
          {previewImage && (
            <button onClick={() => setPreviewImage(null)}
              className="p-1.5 bg-slate-800 hover:bg-slate-700 rounded transition" title="Clear image">
              <X size={14} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} />
          <button onClick={fitView}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded transition">
            <Maximize2 size={14} /> Fit
          </button>
          <button onClick={saveConfig} disabled={saveMutation.isPending}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium transition ${
              dirty ? 'bg-green-500 hover:bg-green-400 text-slate-950' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
            }`}>
            <Save size={14} /> {saveMutation.isPending ? 'Saving...' : dirty ? 'Save' : 'Saved'}
          </button>
          <button onClick={exportConfig}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded font-medium transition">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Export"}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div className="flex-1 relative bg-slate-950 overflow-hidden">
          <svg ref={svgRef} className="w-full h-full canvas-bg"
            onPointerDown={onPointerDownCanvas} onPointerMove={onPointerMove}
            onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
            onWheel={onWheel}
            onDragOver={(e) => e.preventDefault()} onDrop={onDropStation}
            style={{ touchAction: 'none' }}>
            <defs>
              {walls.flatMap((wall) => wall.monitors.map((m) => (
                <clipPath key={`${wall.id}-${m.id}`} id={`scr-${wall.id}-${m.id}`}>
                  <rect x={m.x} y={m.y} width={m.w} height={m.h} />
                </clipPath>
              )))}
            </defs>

            <rect className="canvas-bg" x="0" y="0" width="100%" height="100%" fill="#020617" />

            <g transform={`translate(${view.tx} ${view.ty}) scale(${view.zoom})`}>
              <BackgroundGrid bounds={worldBB} />

              {walls.map((wall, wi) => {
                const isThisSel = selected.wallId === wall.id && !selected.monitorId;
                const isThisActive = selected.wallId === wall.id;
                const vb = visibleBounds(wall.monitors);
                const tb = totalBounds(wall.monitors);
                return (
                  <g key={wall.id} transform={`translate(${wall.origin.x} ${wall.origin.y}) rotate(${wall.rotation})`}>
                    <rect x={tb.x - 80} y={tb.y - 80}
                      width={tb.w + 160} height={tb.h + 160}
                      fill="rgba(15, 23, 42, 0.5)"
                      stroke={isThisSel ? '#22d3ee' : (isThisActive ? '#1e293b' : '#0f1827')}
                      strokeWidth={(isThisSel ? 2 : 1) / view.zoom}
                      strokeDasharray={`${10 / view.zoom} ${6 / view.zoom}`}
                      onPointerDown={(e) => onPointerDownWall(e, wall.id)}
                      style={{ cursor: 'move' }}
                    />
                    <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h}
                      fill="none" stroke="#334155" strokeWidth={1.5 / view.zoom}
                      strokeDasharray={`${5 / view.zoom} ${5 / view.zoom}`}
                      pointerEvents="none" />

                    {wall.monitors.map((m) => {
                      const animFrame = animPreviewFrames.get(`${wall.id}:${m.id}`);
                      return (
                        <g key={`img-${m.id}`} clipPath={`url(#scr-${wall.id}-${m.id})`} pointerEvents="none">
                          {animFrame ? (
                            <image href={animFrame} x={m.x} y={m.y} width={m.w} height={m.h}
                              preserveAspectRatio="none" />
                          ) : previewImage ? (
                            <image href={previewImage} x={vb.x} y={vb.y} width={vb.w} height={vb.h}
                              preserveAspectRatio="none" />
                          ) : (
                            <TestPattern bounds={vb} hueBase={180 + wi * 55} />
                          )}
                        </g>
                      );
                    })}

                    {wall.monitors.map((m) => {
                      const isSel = selected.monitorId === m.id && selected.wallId === wall.id;
                      return (
                        <g key={m.id} onPointerDown={(e) => onPointerDownMonitor(e, wall.id, m.id)}
                           style={{ cursor: 'grab' }}>
                          <rect x={m.x - m.bl} y={m.y - m.bt}
                            width={m.w + m.bl + m.br} height={m.h + m.bt + m.bb}
                            fill="#0a0f17"
                            stroke={isSel ? '#22d3ee' : '#0f1620'}
                            strokeWidth={(isSel ? 4 : 1.5) / view.zoom} />
                          <rect x={m.x - m.bl + 2} y={m.y - m.bt + 2}
                            width={m.w + m.bl + m.br - 4} height={m.h + m.bt + m.bb - 4}
                            fill="none" stroke="#1a2230" strokeWidth={1 / view.zoom} pointerEvents="none" />
                          <rect x={m.x} y={m.y} width={m.w} height={m.h}
                            fill="transparent" stroke="#0c121b" strokeWidth={1 / view.zoom}
                            pointerEvents="none" />
                          <g transform={`translate(${m.x + m.w / 2} ${m.y + m.h + m.bb / 2}) rotate(${-wall.rotation})`}>
                            <text textAnchor="middle" dominantBaseline="middle"
                              fontFamily="ui-monospace, monospace"
                              fontSize={Math.min(m.bb * 0.45, 80 / view.zoom)}
                              fill={isSel ? '#22d3ee' : '#475569'}>{m.name}</text>
                          </g>
                        </g>
                      );
                    })}

                    {guides.wallId === wall.id && guides.list.map((g, i) =>
                      g.type === 'v' ? (
                        <line key={i} x1={g.x!} y1={g.y1!} x2={g.x!} y2={g.y2!}
                          stroke="#fbbf24" strokeWidth={1.5 / view.zoom}
                          strokeDasharray={`${6 / view.zoom} ${4 / view.zoom}`} pointerEvents="none" />
                      ) : (
                        <line key={i} x1={g.x1!} y1={g.y!} x2={g.x2!} y2={g.y!}
                          stroke="#fbbf24" strokeWidth={1.5 / view.zoom}
                          strokeDasharray={`${6 / view.zoom} ${4 / view.zoom}`} pointerEvents="none" />
                      )
                    )}

                    {/* Distance labels between monitors during drag */}
                    {distances.wallId === wall.id && distances.list.map((d, i) => {
                      const mx = (d.x1 + d.x2) / 2, my = (d.y1 + d.y2) / 2;
                      const fontSize = Math.max(36, 18 / view.zoom);
                      const padX = fontSize * 2.2, padY = fontSize * 0.8;
                      const tickLen = 30;
                      return (
                        <g key={`dist-${i}`} pointerEvents="none">
                          {/* Dimension line */}
                          <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
                            stroke="#f97316" strokeWidth={2.5 / view.zoom} />
                          {/* End ticks */}
                          {d.type === 'h' ? (<>
                            <line x1={d.x1} y1={d.y1 - tickLen} x2={d.x1} y2={d.y1 + tickLen}
                              stroke="#f97316" strokeWidth={2 / view.zoom} />
                            <line x1={d.x2} y1={d.y2 - tickLen} x2={d.x2} y2={d.y2 + tickLen}
                              stroke="#f97316" strokeWidth={2 / view.zoom} />
                          </>) : (<>
                            <line x1={d.x1 - tickLen} y1={d.y1} x2={d.x1 + tickLen} y2={d.y1}
                              stroke="#f97316" strokeWidth={2 / view.zoom} />
                            <line x1={d.x2 - tickLen} y1={d.y2} x2={d.x2 + tickLen} y2={d.y2}
                              stroke="#f97316" strokeWidth={2 / view.zoom} />
                          </>)}
                          {/* Label background + text */}
                          <g transform={`translate(${mx} ${my}) rotate(${-wall.rotation})`}>
                            <rect x={-padX / 2} y={-padY / 2 - fontSize * 0.1}
                              width={padX} height={padY} rx={4}
                              fill="#1c1917" stroke="#f97316" strokeWidth={1.5 / view.zoom} />
                            <text textAnchor="middle" dominantBaseline="central"
                              fontFamily="ui-monospace, monospace" fontSize={fontSize}
                              fontWeight={700} fill="#fb923c">
                              {d.dist}mm
                            </text>
                          </g>
                        </g>
                      );
                    })}

                    <g transform={`translate(${tb.x + tb.w / 2} ${tb.y + tb.h + 250}) rotate(${-wall.rotation})`}
                       onPointerDown={(e) => onPointerDownWall(e, wall.id)}
                       style={{ cursor: 'move' }}>
                      <rect x={-220} y={-30} width={440} height={60} rx={6}
                        fill={isThisActive ? '#082f49' : '#0c1322'}
                        stroke={isThisSel ? '#22d3ee' : '#1e293b'}
                        strokeWidth={1.5 / view.zoom} />
                      <text textAnchor="middle" dominantBaseline="middle" y={-2}
                        fontFamily="ui-monospace, monospace" fontSize={28} fontWeight={500}
                        fill="#e2e8f0">{wall.name}</text>
                      <text textAnchor="middle" dominantBaseline="middle" y={20}
                        fontFamily="ui-monospace, monospace" fontSize={18}
                        fill={isThisSel ? '#22d3ee' : '#64748b'}>
                        {wall.rotation.toFixed(0)}&deg; &middot; {wall.monitors.length} stn
                      </text>
                    </g>

                    {isThisSel && (
                      <g pointerEvents="all" style={{ cursor: 'crosshair' }}
                         onPointerDown={(e) => onPointerDownRotate(e, wall.id)}>
                        <line x1={tb.x + tb.w / 2} y1={tb.y - 80}
                              x2={tb.x + tb.w / 2} y2={tb.y - 360}
                              stroke="#22d3ee" strokeWidth={2 / view.zoom} pointerEvents="none" />
                        <circle cx={tb.x + tb.w / 2} cy={tb.y - 380}
                          r={Math.max(80, 50 / view.zoom)}
                          fill="#22d3ee" stroke="#0c4a6e" strokeWidth={3 / view.zoom} />
                        <g transform={`translate(${tb.x + tb.w / 2} ${tb.y - 380}) scale(${Math.max(3, 2 / view.zoom)})`}
                           pointerEvents="none">
                          <RotateIconSVG />
                        </g>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>

            <g pointerEvents="none">
              <rect x="16" y="16" width="280" height={focusedStats ? 96 : 76}
                fill="rgba(15, 23, 42, 0.9)" stroke="#1e293b" rx="4" />
              <text x="28" y="36" fill="#64748b" fontSize="9" fontFamily="ui-monospace, monospace" letterSpacing="0.1em">
                {focusedStats ? `MASTER \u00b7 ${selectedWall!.name.toUpperCase()}` : 'WORLD BOUNDS'}
              </text>
              {focusedStats ? (
                <>
                  <text x="28" y="55" fill="#22d3ee" fontSize="14" fontFamily="ui-monospace, monospace">
                    {focusedStats.masterW.toLocaleString()} &times; {focusedStats.masterH.toLocaleString()} px
                  </text>
                  <text x="28" y="76" fill="#94a3b8" fontSize="11" fontFamily="ui-monospace, monospace">
                    {(focusedStats.vb.w / 1000).toFixed(2)}m &times; {(focusedStats.vb.h / 1000).toFixed(2)}m
                  </text>
                  <text x="28" y="96" fill="#94a3b8" fontSize="11" fontFamily="ui-monospace, monospace">
                    Dead: {focusedStats.deadPct.toFixed(1)}% &middot; Rotation: {selectedWall!.rotation.toFixed(0)}&deg;
                  </text>
                </>
              ) : (
                <>
                  <text x="28" y="55" fill="#22d3ee" fontSize="14" fontFamily="ui-monospace, monospace">
                    {(worldBB.w / 1000).toFixed(2)}m &times; {(worldBB.h / 1000).toFixed(2)}m
                  </text>
                  <text x="28" y="76" fill="#94a3b8" fontSize="11" fontFamily="ui-monospace, monospace">
                    {walls.length} walls &middot; {totalStations} stations
                  </text>
                </>
              )}
            </g>
          </svg>
          <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-[10px] font-mono text-slate-600 pointer-events-none">
            <span>DRAG STATION &middot; DRAG WALL FRAME &middot; DRAG ROTATE HANDLE &middot; WHEEL TO ZOOM &middot; SHIFT BYPASSES SNAP</span>
            <span>{Math.round(view.zoom * 100)}%</span>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="w-80 border-l border-slate-800 bg-slate-900 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-slate-800 shrink-0">
            <button onClick={() => setSidebarTab('properties')}
              className={`flex-1 px-3 py-2 text-xs font-medium transition ${
                sidebarTab === 'properties' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
              }`}>
              Properties
            </button>
            <button onClick={() => setSidebarTab('stations')}
              className={`flex-1 px-3 py-2 text-xs font-medium transition ${
                sidebarTab === 'stations' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
              }`}>
              Stations {stations.length > 0 && <span className="ml-1 text-slate-600">({stations.length})</span>}
            </button>
            <button onClick={() => setSidebarTab('groups')}
              className={`flex-1 px-3 py-2 text-xs font-medium transition ${
                sidebarTab === 'groups' ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-slate-500 hover:text-slate-300'
              }`}>
              Groups {canvasGroups.length > 0 && <span className="ml-1 text-slate-600">({canvasGroups.length})</span>}
            </button>
          </div>

          {sidebarTab === 'properties' ? (
            isMonSel ? (
              <MonitorPanel wall={selectedWall!} mon={selectedMonitor!}
                update={(patch) => updateMonitor(selectedWall!.id, selectedMonitor!.id, patch)}
                onDelete={deleteSelected} />
            ) : isWallSel ? (
              <WallPanel wall={selectedWall!} stats={focusedStats}
                update={(patch) => updateWall(selectedWall!.id, patch)}
                onDelete={deleteSelected} setSelected={setSelected} />
            ) : (
              <OverviewPanel walls={walls} setSelected={setSelected} wallStats={wallStats} />
            )
          ) : sidebarTab === 'stations' ? (
            <StationsPanel stations={stations} placedIds={placedStationIds} />
          ) : (
            <CanvasGroupPanel
              groups={canvasGroups}
              walls={walls}
              selectedGroupId={selectedGroupId}
              onSelectGroup={setSelectedGroupId}
              onAddGroup={addCanvasGroup}
              onDeleteGroup={deleteCanvasGroup}
              onUpdateGroup={updateCanvasGroup}
              getGeometry={getGroupGeometry}
            />
          )}
        </aside>
      </div>

      {/* Bottom panel toggle */}
      <div className="border-t border-slate-800 bg-slate-900">
        <div className="flex border-b border-slate-800/50">
          <button onClick={() => setBottomPanel('export')}
            className={`px-4 py-1.5 text-[10px] font-mono uppercase tracking-wider transition ${
              bottomPanel === 'export' ? 'text-cyan-400 border-b border-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}>
            Export Preview
          </button>
          <button onClick={() => setBottomPanel('animation')}
            className={`px-4 py-1.5 text-[10px] font-mono uppercase tracking-wider transition ${
              bottomPanel === 'animation' ? 'text-cyan-400 border-b border-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}>
            Animation
          </button>
        </div>

        {bottomPanel === 'export' ? (
          <div className="px-4 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Per-station export preview
              </span>
              <div className="flex-1 h-px bg-slate-800" />
            </div>
            <div className="flex gap-6">
              {walls.map((wall) => {
                const vb = visibleBounds(wall.monitors);
                return (
                  <div key={wall.id} className="flex-shrink-0">
                    <button onClick={() => setSelected({ wallId: wall.id, monitorId: null })}
                      className="text-[10px] font-mono text-slate-400 mb-1 hover:text-cyan-400 transition flex items-center gap-1.5">
                      {wall.name} <span className="text-slate-600">&middot; {wall.rotation.toFixed(0)}&deg;</span>
                    </button>
                    <div className="flex gap-2">
                      {wall.monitors.map((m) => (
                        <SlicePreview key={m.id} monitor={m} bounds={vb}
                          previewImage={previewImage}
                          onClick={() => setSelected({ wallId: wall.id, monitorId: m.id })}
                          isSelected={selected.wallId === wall.id && selected.monitorId === m.id} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <AnimationTimeline
            animations={animations}
            groups={canvasGroups}
            getGeometry={getGroupGeometry}
            onUpdateAnimations={setAnimations}
            onPreviewFrame={handlePreviewFrame}
            onPreviewClear={handlePreviewClear}
          />
        )}
      </div>
      </>)}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────
const RotateIconSVG = () => (
  <g transform="translate(-12, -12)">
    <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.78 1 6.42 2.58" fill="none"
      stroke="#0c4a6e" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 3v6h-6" fill="none" stroke="#0c4a6e" strokeWidth={2.5}
      strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const BackgroundGrid = ({ bounds }: { bounds: Bounds }) => {
  const pad = 1500;
  const x0 = Math.floor((bounds.x - pad) / 100) * 100;
  const y0 = Math.floor((bounds.y - pad) / 100) * 100;
  const x1 = Math.ceil((bounds.x + bounds.w + pad) / 100) * 100;
  const y1 = Math.ceil((bounds.y + bounds.h + pad) / 100) * 100;
  const lines = [];
  for (let x = x0; x <= x1; x += 100) {
    lines.push(<line key={`v${x}`} x1={x} y1={y0} x2={x} y2={y1}
      stroke={x % 1000 === 0 ? '#1e293b' : '#0f1827'} strokeWidth={x % 1000 === 0 ? 1.5 : 0.5} />);
  }
  for (let y = y0; y <= y1; y += 100) {
    lines.push(<line key={`h${y}`} x1={x0} y1={y} x2={x1} y2={y}
      stroke={y % 1000 === 0 ? '#1e293b' : '#0f1827'} strokeWidth={y % 1000 === 0 ? 1.5 : 0.5} />);
  }
  return <g pointerEvents="none">{lines}</g>;
};

const MonitorPanel = ({ wall, mon, update, onDelete }: { wall: WallDef; mon: MonitorDef; update: (p: Partial<MonitorDef>) => void; onDelete: () => void }) => (
  <div className="flex flex-col h-full">
    <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Station &middot; {wall.name}</div>
        <div className="text-sm font-medium mt-0.5">{mon.name}</div>
      </div>
      <button onClick={onDelete} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition">
        <Trash2 size={14} />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto p-4 space-y-5">
      <Section title="Identity">
        <TextInput label="Name" value={mon.name} onChange={(v) => update({ name: v })} />
        <div className="text-[10px] font-mono text-slate-600 pt-1">ID &middot; {mon.id}</div>
      </Section>
      <Section title="Position (wall-local)">
        <NumInput label="X" value={mon.x} onChange={(v) => update({ x: v })} />
        <NumInput label="Y" value={mon.y} onChange={(v) => update({ y: v })} />
      </Section>
      <Section title="Physical (visible)">
        <NumInput label="Width"  value={mon.w} onChange={(v) => update({ w: v })} />
        <NumInput label="Height" value={mon.h} onChange={(v) => update({ h: v })} />
      </Section>
      <Section title="Bezels">
        <NumInput label="Top"    value={mon.bt} onChange={(v) => update({ bt: v })} />
        <NumInput label="Right"  value={mon.br} onChange={(v) => update({ br: v })} />
        <NumInput label="Bottom" value={mon.bb} onChange={(v) => update({ bb: v })} />
        <NumInput label="Left"   value={mon.bl} onChange={(v) => update({ bl: v })} />
      </Section>
      <Section title="Native resolution">
        <NumInput label="Width"  value={mon.nw} onChange={(v) => update({ nw: v })} suffix="px" />
        <NumInput label="Height" value={mon.nh} onChange={(v) => update({ nh: v })} suffix="px" />
      </Section>
      <Section title="Quick set">
        <div className="grid grid-cols-2 gap-1.5 max-h-60 overflow-y-auto pr-1">
          {Object.entries(PRESETS).map(([name, p]) => (
            <button key={name} onClick={() => update({ ...p, preset: name })}
              className={`text-left px-2 py-1.5 text-[11px] font-mono border rounded transition ${
                mon.preset === name ? 'bg-cyan-950 border-cyan-700 text-cyan-300' : 'bg-slate-950 hover:bg-slate-800 border-slate-800'
              }`}>
              <span className={mon.preset === name ? 'text-cyan-300' : 'text-slate-300'}>{name}</span>
            </button>
          ))}
        </div>
      </Section>
    </div>
  </div>
);

const QUICK_ANGLES = [0, 45, 90, 135, 180, -135, -90, -45];

const WallPanel = ({ wall, stats, update, onDelete, setSelected }: {
  wall: WallDef; stats: ReturnType<typeof Object> | null; update: (p: Partial<WallDef>) => void;
  onDelete: () => void; setSelected: (s: { wallId: string | null; monitorId: string | null }) => void;
}) => {
  const [hGap, setHGap] = useState(0);
  const [vGap, setVGap] = useState(0);

  // Compute current horizontal gap from first two monitors (if side-by-side)
  useEffect(() => {
    if (wall.monitors.length < 2) return;
    const sorted = [...wall.monitors].sort((a, b) => a.x - b.x);
    const gap = sorted[1].x - (sorted[0].x + sorted[0].w + sorted[0].br + sorted[1].bl);
    setHGap(Math.round(gap));
  }, [wall.id]); // only on wall switch, not every monitor move

  const distributeH = () => {
    if (wall.monitors.length < 2) return;
    const sorted = [...wall.monitors].sort((a, b) => a.x - b.x);
    let x = sorted[0].x;
    const reordered = sorted.map((m, i) => {
      const newM = { ...m, x: Math.round(x) };
      x += m.w + m.br + (i < sorted.length - 1 ? sorted[i + 1].bl : 0) + hGap;
      return newM;
    });
    update({ monitors: wall.monitors.map((m) => reordered.find((r) => r.id === m.id) ?? m) });
  };

  const distributeV = () => {
    if (wall.monitors.length < 2) return;
    const sorted = [...wall.monitors].sort((a, b) => a.y - b.y);
    let y = sorted[0].y;
    const reordered = sorted.map((m, i) => {
      const newM = { ...m, y: Math.round(y) };
      y += m.h + m.bb + (i < sorted.length - 1 ? sorted[i + 1].bt : 0) + vGap;
      return newM;
    });
    update({ monitors: wall.monitors.map((m) => reordered.find((r) => r.id === m.id) ?? m) });
  };

  return (
  <div className="flex flex-col h-full">
    <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <Layers size={10} /> Wall
        </div>
        <div className="text-sm font-medium mt-0.5">{wall.name}</div>
      </div>
      <button onClick={onDelete} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition">
        <Trash2 size={14} />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto p-4 space-y-5">
      <Section title="Identity">
        <TextInput label="Name" value={wall.name} onChange={(v) => update({ name: v })} />
      </Section>
      <Section title="Origin (world)">
        <NumInput label="X" value={Math.round(wall.origin.x)}
          onChange={(v) => update({ origin: { ...wall.origin, x: v } })} />
        <NumInput label="Y" value={Math.round(wall.origin.y)}
          onChange={(v) => update({ origin: { ...wall.origin, y: v } })} />
      </Section>
      <Section title="Rotation">
        <div className="flex items-center gap-2">
          <input type="range" min={-180} max={180} step={1}
            value={Math.round(wall.rotation)}
            onChange={(e) => update({ rotation: Number(e.target.value) })}
            className="flex-1 accent-cyan-500" />
          <input type="number" value={Math.round(wall.rotation)}
            onChange={(e) => update({ rotation: normRot(Number(e.target.value)) })}
            className="w-16 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500" />
          <span className="text-slate-500 font-mono text-[10px]">&deg;</span>
        </div>
        <div className="flex flex-wrap gap-1 pt-1">
          {QUICK_ANGLES.map((a) => (
            <button key={a} onClick={() => update({ rotation: a })}
              className={`px-2 py-1 text-[10px] font-mono rounded border transition ${
                Math.round(wall.rotation) === a
                  ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                  : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}>
              {a}&deg;
            </button>
          ))}
        </div>
      </Section>
      {wall.monitors.length >= 2 && (
        <Section title="Distribute">
          <div className="flex items-center gap-2">
            <NumInput label="H gap" value={hGap} onChange={setHGap} />
            <button onClick={distributeH}
              className="px-2 py-1 text-[10px] font-mono bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition whitespace-nowrap">
              Apply
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NumInput label="V gap" value={vGap} onChange={setVGap} />
            <button onClick={distributeV}
              className="px-2 py-1 text-[10px] font-mono bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition whitespace-nowrap">
              Apply
            </button>
          </div>
          <p className="text-[10px] text-slate-600 pt-0.5">Gap between bezel edges (mm). 0 = flush.</p>
        </Section>
      )}
      {stats && (
        <Section title="Master canvas">
          <Stat label="Render dim" value={`${(stats as any).masterW.toLocaleString()} \u00d7 ${(stats as any).masterH.toLocaleString()} px`} />
          <Stat label="Wall span"  value={`${((stats as any).vb.w/1000).toFixed(2)}m \u00d7 ${((stats as any).vb.h/1000).toFixed(2)}m`} />
          <Stat label="Density"    value={`${(stats as any).density.toFixed(2)} px/mm`} />
          <Stat label="Dead space" value={`${(stats as any).deadPct.toFixed(1)}%`}
            accent={(stats as any).deadPct > 60 ? 'warn' : null} />
        </Section>
      )}
      <Section title={`Stations (${wall.monitors.length})`}>
        <div className="space-y-1">
          {wall.monitors.map((m) => (
            <button key={m.id} onClick={() => setSelected({ wallId: wall.id, monitorId: m.id })}
              className="w-full flex items-center justify-between px-2 py-1.5 text-xs bg-slate-950 border border-slate-800 hover:border-cyan-700 rounded transition">
              <span className="text-slate-200">{m.name}</span>
              <span className="font-mono text-slate-500 text-[10px]">{m.x},{m.y} &middot; {m.nw}&times;{m.nh}</span>
            </button>
          ))}
        </div>
      </Section>
    </div>
  </div>
  );
};

const OverviewPanel = ({ walls, setSelected, wallStats }: {
  walls: WallDef[];
  setSelected: (s: { wallId: string | null; monitorId: string | null }) => void;
  wallStats: (w: WallDef) => any;
}) => {
  const total = walls.reduce((s, w) => s + w.monitors.length, 0);
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-800">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Overview</div>
        <div className="text-sm font-medium mt-0.5">Floor plan</div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Section title="Layout">
          <Stat label="Walls" value={walls.length} />
          <Stat label="Stations" value={total} />
        </Section>
        <Section title="Walls">
          <div className="space-y-2">
            {walls.map((wall) => {
              const stats = wallStats(wall);
              return (
                <div key={wall.id} className="bg-slate-950 border border-slate-800 rounded">
                  <button onClick={() => setSelected({ wallId: wall.id, monitorId: null })}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-900 transition border-b border-slate-800">
                    <div className="text-left">
                      <div className="text-xs text-slate-200 font-medium">{wall.name}</div>
                      <div className="text-[10px] font-mono text-slate-500">
                        {wall.rotation.toFixed(0)}&deg; &middot; ({Math.round(wall.origin.x)}, {Math.round(wall.origin.y)})
                        {stats ? ` \u00b7 ${stats.masterW}\u00d7${stats.masterH}px` : ''}
                      </div>
                    </div>
                    <RotateCw size={12} className="text-slate-600" />
                  </button>
                  <div className="px-2 py-1.5 space-y-0.5">
                    {wall.monitors.map((m) => (
                      <button key={m.id} onClick={() => setSelected({ wallId: wall.id, monitorId: m.id })}
                        className="w-full flex items-center justify-between px-2 py-1 text-[11px] hover:bg-slate-900 rounded transition">
                        <span className="text-slate-300">{m.name}</span>
                        <span className="font-mono text-slate-600 text-[10px]">{m.nw}&times;{m.nh}</span>
                      </button>
                    ))}
                    {wall.monitors.length === 0 && (
                      <div className="text-[10px] text-slate-600 px-2 py-1 italic">No stations</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
        <div className="text-[10px] text-slate-500 leading-relaxed pt-3 border-t border-slate-800">
          Each wall is its own slicing plane. Continuous-motion video works <em>within</em> a wall.
          Cross-wall transitions should be designed as intentional handoffs.
        </div>
      </div>
    </div>
  );
};

const StationsPanel = ({ stations, placedIds }: {
  stations: { id: string; name: string; machine_id: string }[];
  placedIds: Set<string>;
}) => {
  const unplaced = stations.filter((s) => !placedIds.has(s.id));
  const placed = stations.filter((s) => placedIds.has(s.id));

  const onDragStart = (e: React.DragEvent, station: { id: string; name: string }) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ stationId: station.id, stationName: station.name }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-800">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Location stations</div>
        <div className="text-sm font-medium mt-0.5">{stations.length} station{stations.length !== 1 ? 's' : ''}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {stations.length === 0 && (
          <p className="text-xs text-slate-500 italic">No stations assigned to this location.</p>
        )}
        {unplaced.length > 0 && (
          <Section title={`Unplaced (${unplaced.length})`}>
            <div className="space-y-1">
              {unplaced.map((s) => (
                <div key={s.id} draggable
                  onDragStart={(e) => onDragStart(e, s)}
                  className="flex items-center gap-2 px-2 py-2 text-xs bg-slate-950 border border-slate-800 hover:border-cyan-700 rounded cursor-grab active:cursor-grabbing transition">
                  <Monitor size={14} className="text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 truncate">{s.name}</div>
                    <div className="text-[10px] font-mono text-slate-600 truncate">{s.machine_id}</div>
                  </div>
                  <span className="text-[10px] text-cyan-600 font-mono shrink-0">DRAG</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-600 pt-1">Drag onto the canvas to place.</p>
          </Section>
        )}
        {placed.length > 0 && (
          <Section title={`Placed (${placed.length})`}>
            <div className="space-y-1">
              {placed.map((s) => (
                <div key={s.id}
                  className="flex items-center gap-2 px-2 py-2 text-xs bg-slate-950 border border-slate-800 rounded opacity-60">
                  <Check size={14} className="text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-400 truncate">{s.name}</div>
                    <div className="text-[10px] font-mono text-slate-700 truncate">{s.machine_id}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

const SlicePreview = ({ monitor, bounds, previewImage, onClick, isSelected }: {
  monitor: MonitorDef; bounds: Bounds; previewImage: string | null; onClick: () => void; isSelected: boolean;
}) => {
  const aspect = monitor.w / monitor.h;
  const PREVIEW_H = 56;
  const PREVIEW_W = PREVIEW_H * aspect;
  let bgStyle: React.CSSProperties = {};
  if (previewImage && bounds.w > 0 && bounds.h > 0) {
    const sx = PREVIEW_W / monitor.w, sy = PREVIEW_H / monitor.h;
    bgStyle = {
      backgroundImage: `url(${previewImage})`,
      backgroundSize: `${bounds.w * sx}px ${bounds.h * sy}px`,
      backgroundPosition: `${-(monitor.x - bounds.x) * sx}px ${-(monitor.y - bounds.y) * sy}px`,
      backgroundRepeat: 'no-repeat',
    };
  }
  return (
    <button onClick={onClick}
      className={`flex flex-col gap-1 transition ${isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}>
      <div className={`relative rounded-sm overflow-hidden border ${isSelected ? 'border-cyan-400' : 'border-slate-800'}`}
        style={{ width: PREVIEW_W, height: PREVIEW_H, ...bgStyle }}>
        {!previewImage && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-700">
            <Square size={12} />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-1" style={{ width: PREVIEW_W }}>
        <span className="text-[9px] font-mono text-slate-400 truncate">{monitor.name}</span>
      </div>
    </button>
  );
};
